import { Card } from "@/components/ui/card";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

export default function TemperatureChart({ trends }) {
  const chartData = trends.map((t, i) => {
    const ts = t.timestamp || "";
    const timePart = ts.split(" ")[1] || "";
    const shortTime = timePart.substring(0, 5);
    const lightVal = (t.light || "").toLowerCase();
    return {
      index: i,
      time: shortTime || `#${i}`,
      temperature: t.temperature,
      humidity: t.humidity,
      light: lightVal !== "dark" && lightVal !== "" ? 1 : 0,
    };
  });

  // Show every Nth label to avoid crowding
  const labelInterval = Math.max(1, Math.floor(chartData.length / 12));

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-xs font-mono">
        <p className="text-zinc-400 mb-1.5">{label}</p>
        {payload.map((p, i) => {
          if (p.dataKey === "light") {
            return (
              <p key={i} className="text-amber-400 leading-relaxed">
                Light: {p.value === 1 ? "Detected" : "Dark"}
              </p>
            );
          }
          return (
            <p key={i} style={{ color: p.color }} className="leading-relaxed">
              {p.name}: {p.value}
              {p.name === "Temperature" ? "\u00b0F" : "%"}
            </p>
          );
        })}
      </div>
    );
  };

  // Check if any light was detected
  const hasLightExposure = chartData.some((d) => d.light === 1);

  return (
    <Card
      data-testid="temperature-chart"
      className="bg-zinc-900 border-zinc-800 rounded-sm p-4"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
          Temperature, Humidity & Light Detection
        </h3>
        {hasLightExposure && (
          <span className="text-[10px] font-mono text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-sm">
            Light Exposure Detected
          </span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#333333" />
          <XAxis
            dataKey="time"
            interval={labelInterval}
            tick={{
              fill: "#A0A0A0",
              fontSize: 10,
              fontFamily: "IBM Plex Mono",
            }}
            stroke="#333333"
          />
          <YAxis
            yAxisId="left"
            tick={{
              fill: "#A0A0A0",
              fontSize: 10,
              fontFamily: "IBM Plex Mono",
            }}
            stroke="#333333"
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 1]}
            hide={!hasLightExposure}
            tick={false}
            stroke="#333333"
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{
              fontSize: 11,
              fontFamily: "IBM Plex Mono",
              paddingTop: 8,
            }}
          />
          {hasLightExposure && (
            <Area
              yAxisId="right"
              type="stepAfter"
              dataKey="light"
              name="Light"
              fill="#F59E0B"
              fillOpacity={0.15}
              stroke="#F59E0B"
              strokeWidth={0}
            />
          )}
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="temperature"
            name="Temperature"
            stroke="#00DCC2"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: "#00DCC2" }}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="humidity"
            name="Humidity"
            stroke="#10B981"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: "#10B981" }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Card>
  );
}
